import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ReactSpeedometer from "react-d3-speedometer";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Add this component definition before the main return statement
const PerformanceSpeedometer = ({ score }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-8 bg-rich_black-400/30 backdrop-blur-sm rounded-xl border border-rich_black-300 shadow-lg"
    >
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-tiffany_blue-400 to-dark_cyan-400 text-transparent bg-clip-text">
          Performance Score
        </h3>
        <p className="text-vanilla-200 mt-1 text-sm">
          Visual representation of your website's performance
        </p>
      </div>

      <div className="flex justify-center mb-2">
        <ReactSpeedometer
          maxValue={100}
          value={score}
          needleColor="#0E9F6E"
          startColor="#DB4437"
          segments={3}
          endColor="#0E9F6E"
          needleTransition="easeElastic"
          needleTransitionDuration={2000}
          width={280}
          height={180}
          textColor="#FFFFFF"
          segmentColors={['#DB4437', '#F4B400', '#0E9F6E']}
          currentValueText={`${score}/100`}
          customSegmentStops={[0, 50, 90, 100]}
          customSegmentLabels={[
            { 
              text: 'Needs Work', 
              position: 'OUTSIDE',
              color: '#F87171',
              fontSize: '12px'
            },
            { 
              text: 'Good', 
              position: 'OUTSIDE',
              color: '#FBBF24',
              fontSize: '12px'
            },
            { 
              text: 'Excellent', 
              position: 'OUTSIDE',
              color: '#34D399',
              fontSize: '12px'
            }
          ]}
          ringWidth={20}
          needleHeightRatio={0.7}
        />
      </div>

      <div className="mt-8 flex justify-between px-4">
        <div className="text-center">
          <div className="w-3 h-3 bg-rufous-400 rounded-full mx-auto mb-1"></div>
          <span className="text-xs text-vanilla-300">0-49</span>
          <p className="text-xs font-medium text-rufous-300">Needs Work</p>
        </div>
        <div className="text-center">
          <div className="w-3 h-3 bg-amber-400 rounded-full mx-auto mb-1"></div>
          <span className="text-xs text-vanilla-300">50-89</span>
          <p className="text-xs font-medium text-amber-300">Good</p>
        </div>
        <div className="text-center">
          <div className="w-3 h-3 bg-tiffany_blue-400 rounded-full mx-auto mb-1"></div>
          <span className="text-xs text-vanilla-300">90-100</span>
          <p className="text-xs font-medium text-tiffany_blue-300">Excellent</p>
        </div>
      </div>

      {score >= 90 ? (
        <motion.div 
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          className="mt-6 text-center"
        >
          <p className="text-sm bg-gradient-to-r from-tiffany_blue-400 to-emerald-400 text-transparent bg-clip-text font-medium">
            🎉 Excellent performance! Keep it up!
          </p>
        </motion.div>
      ) : score >= 50 ? (
        <p className="mt-6 text-center text-sm text-amber-300">
          👍 Good score, but there's room for improvement
        </p>
      ) : (
        <p className="mt-6 text-center text-sm text-rufous-300">
          ⚠️ Needs optimization work to improve performance
        </p>
      )}
    </motion.div>
  );
};

const GoogleAnalyticsApi = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [url, setUrl] = useState('https://web.dev/');

  useEffect(() => {
    const fetchPageSpeedData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const apiKey = process.env.REACT_APP_PAGE_SPEED_API;
        if (!apiKey) {
          throw new Error('API key not configured');
        }
        
        const response = await fetch(
          `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(url)}&key=${apiKey}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setPageData(data);
      } catch (err) {
        console.error('Fetching PageSpeed Insights failed:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPageSpeedData();
  }, [url]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newUrl = formData.get('url');
    if (newUrl) {
      setUrl(newUrl);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full relative bg-white pt-24 p-6 overflow-x-hidden">
        <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-24 -left-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-blue-900 via-purple-950 from-20% to-teal-400'></span>
        <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-0 -right-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-dark_cyan-100 bg-gradient-to-br from-blue-800 via-purple-400 from-20% to-cyan-300'></span>
        <span className='absolute motion-safe:animate-bounce-slow opacity-65 -z-1 bottom-0 -ml-36 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-teal-800 to-teal-500'></span>
        
        <div className="max-w-6xl mx-auto flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-dark_cyan-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full relative bg-white pt-24 p-6 overflow-x-hidden">
        <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-24 -left-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-blue-900 via-purple-950 from-20% to-teal-400'></span>
        <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-0 -right-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-dark_cyan-100 bg-gradient-to-br from-blue-800 via-purple-400 from-20% to-cyan-300'></span>
        <span className='absolute motion-safe:animate-bounce-slow opacity-65 -z-1 bottom-0 -ml-36 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-teal-800 to-teal-500'></span>
        
        <div className="max-w-6xl mx-auto">
          <div className="p-4 bg-rufous-500/20 border border-rufous-500 rounded-lg text-rufous-100">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) return null;

  // Extract CrUX metrics
  const cruxMetrics = {
    'First Contentful Paint':
      pageData.loadingExperience?.metrics?.FIRST_CONTENTFUL_PAINT_MS?.category || 'N/A',
    'Interaction to Next Paint':
      pageData.loadingExperience?.metrics?.INTERACTION_TO_NEXT_PAINT?.category || 'N/A',
  };

  // Extract Lighthouse metrics
  const lighthouse = pageData.lighthouseResult;
  const lighthouseMetrics = {
    'First Contentful Paint':
      lighthouse.audits['first-contentful-paint']?.displayValue || 'N/A',
    'Speed Index': lighthouse.audits['speed-index']?.displayValue || 'N/A',
    'Largest Contentful Paint':
      lighthouse.audits['largest-contentful-paint']?.displayValue || 'N/A',
    'Total Blocking Time':
      lighthouse.audits['total-blocking-time']?.displayValue || 'N/A',
    'Time To Interactive': lighthouse.audits['interactive']?.displayValue || 'N/A',
  };

  // Prepare data for the chart
  const chartData = {
    labels: Object.keys(lighthouseMetrics),
    datasets: [
      {
        label: 'Performance Metrics (ms)',
        data: Object.values(lighthouseMetrics).map(value => {
          if (typeof value !== 'string') return 0;
          const num = parseFloat(value.replace(/[^\d.]/g, ''));
          return isNaN(num) ? 0 : num;
        }),
        backgroundColor: [
          'rgba(66, 133, 244, 0.7)',
          'rgba(234, 67, 53, 0.7)',
          'rgba(251, 188, 5, 0.7)',
          'rgba(52, 168, 83, 0.7)',
          'rgba(171, 71, 188, 0.7)',
        ],
        borderColor: [
          'rgba(66, 133, 244, 1)',
          'rgba(234, 67, 53, 1)',
          'rgba(251, 188, 5, 1)',
          'rgba(52, 168, 83, 1)',
          'rgba(171, 71, 188, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#ffffff',
          font: {
            family: 'inherit'
          }
        }
      },
      title: {
        display: true,
        text: 'Lighthouse Performance Metrics',
        color: '#ffffff',
        font: {
          family: 'inherit',
          size: 16
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Time (ms)',
          color: '#ffffff'
        },
        ticks: {
          color: '#ffffff'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      x: {
        ticks: {
          color: '#ffffff'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    },
  };

  const getStatusColor = (value) => {
    if (value === 'FAST' || value === 'GOOD') return 'bg-tiffany_blue-500';
    if (value === 'AVERAGE' || value === 'NEEDS IMPROVEMENT') return 'bg-amber-500';
    if (value === 'SLOW' || value === 'POOR') return 'bg-rufous-500';
    return 'bg-dark_cyan-500';
  };

  return (
    <div className="min-h-screen w-full relative bg-white pt-24 p-6 overflow-x-hidden">
      <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-24 -left-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-blue-900 via-purple-950 from-20% to-teal-400'></span>
      <span className='absolute motion-safe:animate-spin-slow opacity-65 -z-1 top-0 -right-40 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-dark_cyan-100 bg-gradient-to-br from-blue-800 via-purple-400 from-20% to-cyan-300'></span>
      <span className='absolute motion-safe:animate-bounce-slow opacity-65 -z-1 bottom-0 -ml-36 w-96 h-96 rounded-full blur-2xl shadow-lg shadow-emerald-400 bg-gradient-to-br from-teal-800 to-teal-500'></span>
      
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex flex-col items-center"
        >
          <h1 className="bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text text-6xl font-bold py-5 w-fit">PageSpeed Insights</h1>
          <p className="bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text w-fit text-2xl">
            Analyze website performance with Google's PageSpeed API
          </p>
        </motion.div>

        <div className="bg-transparent rounded-lg backdrop-blur-md shadow-xl rounded-xll overflow-hidden">
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <label htmlFor="page-url" className="block text-sm font-medium mb-2">
                  Enter website URL to analyze
                </label>
                <form onSubmit={handleSubmit} className="flex gap-4">
                  <input
                    type="url"
                    id="page-url"
                    name="url"
                    defaultValue={url}
                    placeholder="https://example.com"
                    required
                    className="flex-1 bg-gradient-to-br from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 border border-fixify-primary rounded-lg px-4 py-3 focus:ring-2 focus:ring-fixify-lime focus:border-transparent"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-vanilla-500 rounded-lg font-medium transition-all"
                  >
                    Analyze
                  </button>
                </form>
              </div>

              <div className="text-sm text-vanilla-300">
                Analyzing: <span className="font-bold">{pageData.id}</span>
              </div>

              {/* CrUX Metrics */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text">
                  Chrome User Experience (CrUX) Report
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(cruxMetrics).map(([key, value]) => (
                    <div key={key} className={`p-4 rounded-lg border ${value === 'FAST' ? 'border-tiffany_blue-500 bg-tiffany_blue-500/10' : value === 'AVERAGE' ? 'border-amber-500 bg-amber-500/10' : 'border-rufous-500 bg-rufous-500/10'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full ${getStatusColor(value)} flex items-center justify-center text-white`}>
                          {value === 'FAST' ? '🚀' : value === 'AVERAGE' ? '⚠️' : '🐢'}
                        </div>
                        <div>
                          <h4 className="font-medium">{key}</h4>
                          <p className={`text-lg font-bold ${value === 'FAST' ? 'text-tiffany_blue-300' : value === 'AVERAGE' ? 'text-amber-300' : 'text-rufous-300'}`}>
                            {value}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lighthouse Metrics */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold bg-gradient-to-r from-fixify-darkBg to-dark_cyan-500 text-transparent bg-clip-text">
                  Lighthouse Performance Metrics
                </h3>
                <PerformanceSpeedometer score={Math.round(lighthouse.categories.performance.score * 100)} />

                <div className="bg-rich_black-400/70 backdrop-blur-lg p-4 rounded-lg">
                  <Bar data={chartData} options={chartOptions} />
                </div>
                
                <div className="space-y-6">
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <h3 className="text-2xl font-bold text-center mb-6">
      <span className="bg-gradient-to-r from-amber-400 via-tiffany_blue-400 to-emerald-400 text-transparent bg-clip-text">
        Performance Metrics
      </span>
      <div className="mt-2 w-24 h-1 bg-gradient-to-r from-amber-500 to-emerald-500 mx-auto rounded-full" />
    </h3>
    
    <div className="grid grid-cols-1 gap-6">
      {Object.entries(lighthouseMetrics).map(([key, value]) => {
        const numValue = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
        const percentage = Math.min((numValue / 5000) * 100, 100);
        const statusColor = percentage < 33 ? 'bg-emerald-400' : percentage < 66 ? 'bg-amber-400' : 'bg-rufous-400';
        const statusText = percentage < 33 ? 'Fast' : percentage < 66 ? 'Moderate' : 'Slow';
        const statusIcon = percentage < 33 ? '🚀' : percentage < 66 ? '⚡' : '🐢';
        
        return (
          <motion.div
            key={key}
            whileHover={{ scale: 1.02 }}
            className="p-6 bg-gradient-to-br from-rich_black-500 to-rich_black-600 rounded-2xl border border-rich_black-400/30 backdrop-blur-sm shadow-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${statusColor}/10 border ${statusColor}/30 flex items-center justify-center text-xl`}>
                  {statusIcon}
                </div>
                <h4 className="text-lg font-semibold text-vanilla-200">{key}</h4>
              </div>
              <span className="text-2xl font-bold text-vanilla-100">
                {value}
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="w-full bg-rich_black-400 rounded-full h-3 overflow-hidden">
                  <div 
                    className={`h-full ${statusColor} bg-gradient-to-r ${percentage < 33 ? 'from-emerald-400 to-teal-400' : percentage < 66 ? 'from-amber-400 to-orange-400' : 'from-rufous-400 to-red-400'}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
              <span className={`text-sm font-medium ${percentage < 33 ? 'text-emerald-300' : percentage < 66 ? 'text-amber-300' : 'text-rufous-300'}`}>
                {statusText}
              </span>
            </div>
            
            <div className="mt-3 flex justify-between text-xs text-vanilla-300/80">
              <span>0ms</span>
              <span>2500ms</span>
              <span>5000ms</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  </motion.div>
</div>
              </div>

              {/* Test Configuration */}
              <div className="space-y-6">
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.2 }}
  >
    <h3 className="text-2xl font-bold text-center mb-6">
      <span className="bg-gradient-to-r from-tiffany_blue-400 via-dark_cyan-400 to-emerald-400 text-transparent bg-clip-text">
        Test Configuration
      </span>
      <div className="mt-2 w-20 h-1 bg-gradient-to-r from-tiffany_blue-500 to-dark_cyan-500 mx-auto rounded-full" />
    </h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Device Card */}
      <motion.div
        whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
        className="p-6 bg-gradient-to-br from-rich_black-500 to-rich_black-600 rounded-2xl border border-rich_black-400/30 backdrop-blur-sm shadow-xl overflow-hidden"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-tiffany_blue-500/10 rounded-lg border border-tiffany_blue-400/30">
            <svg className="w-6 h-6 text-tiffany_blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-vanilla-200/80 mb-1">Device</h4>
            <p className="text-xl font-bold text-tiffany_blue-300">
              {lighthouse.configSettings.emulatedFormFactor || 'Desktop'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Lighthouse Version Card */}
      <motion.div
        whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
        className="p-6 bg-gradient-to-br from-rich_black-500 to-rich_black-600 rounded-2xl border border-rich_black-400/30 backdrop-blur-sm shadow-xl overflow-hidden"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-dark_cyan-500/10 rounded-lg border border-dark_cyan-400/30">
            <svg className="w-6 h-6 text-dark_cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-vanilla-200/80 mb-1">Lighthouse Version</h4>
            <p className="text-xl font-bold text-dark_cyan-300">
              {lighthouse.lighthouseVersion}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Performance Score Card */}
      <motion.div
        whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
        className="p-6 bg-gradient-to-br from-rich_black-500 to-rich_black-600 rounded-2xl border border-rich_black-400/30 backdrop-blur-sm shadow-xl overflow-hidden"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-400/30">
            <svg className="w-6 h-6 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-vanilla-200/80 mb-1">Performance Score</h4>
            <div className="flex items-baseline gap-2">
              <p className="text-xl font-bold text-emerald-300">
                {Math.round(lighthouse.categories.performance.score * 100)}
              </p>
              <span className="text-vanilla-300/70">/ 100</span>
            </div>
          </div>
        </div>
        <div className="mt-4 h-1.5 bg-rich_black-400 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-emerald-400 to-teal-400"
            style={{ width: `${Math.round(lighthouse.categories.performance.score * 100)}%` }}
          />
        </div>
      </motion.div>

      {/* Fetch Time Card */}
      <motion.div
        whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
        className="p-6 bg-gradient-to-br from-rich_black-500 to-rich_black-600 rounded-2xl border border-rich_black-400/30 backdrop-blur-sm shadow-xl overflow-hidden"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-400/30">
            <svg className="w-6 h-6 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-vanilla-200/80 mb-1">Fetch Time</h4>
            <p className="text-xl font-bold text-purple-300">
              {new Date(pageData.analysisUTCTimestamp).toLocaleString()}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </motion.div>
</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleAnalyticsApi;